import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertTriangle,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import { submitSignupRequest } from "@/lib/api";

const APP_NAME = import.meta.env.VITE_APP_NAME || "Hospital Management System";

export default function SignUpPage() {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    document.title = `Sign up | ${APP_NAME}`;
    return () => { document.title = APP_NAME; };
  }, []);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !phone.trim()) return;

    setStatus("loading");
    setMessage("");
    try {
      await submitSignupRequest({
        full_name: fullName.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
      });
      setStatus("success");
      setMessage("Thank you. We'll be in touch shortly.");
    } catch (err: any) {
      setStatus("error");
      setMessage(err?.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background text-foreground">
      {/* Left: Branding */}
      <div className="hidden lg:flex lg:w-[44%] flex-col justify-between bg-gradient-to-br from-primary via-primary/95 to-primary/85 p-8 xl:p-12 text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative z-10">
          <img src="/onecheckup-logo-primary.png" alt={APP_NAME} className="w-60 xl:w-72 object-contain brightness-0 invert mb-10" />
          <h2 className="text-2xl xl:text-3xl font-bold leading-tight max-w-md mb-3">
            Get started with OneCheckUp
          </h2>
          <p className="text-primary-foreground/80 text-sm max-w-sm leading-relaxed">
            Request access and our team will set up your clinic account. You'll be up and running in minutes.
          </p>
        </div>
        <p className="text-[10px] text-primary-foreground/70 relative z-10">Your clinic data is encrypted and securely stored.</p>
      </div>

      {/* Right: Form - same layout as Login */}
      <div className="flex-1 flex flex-col justify-center items-center px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10 bg-background">
        <div className="w-full max-w-[380px]">
          <div className="lg:hidden flex items-center justify-center mb-6">
            <img src="/onecheckup-logo-primary.png" alt={APP_NAME} className="w-56 object-contain" />
          </div>

          <Link
            to="/login"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to sign in
          </Link>

          <h1 className="text-xl font-semibold text-foreground mb-0.5">Sign up</h1>
          <p className="text-muted-foreground text-sm mb-5">
            We need a few details so we can get in touch with you.
          </p>

          {status === "success" ? (
            <Alert className="mb-4 border-border bg-success/10 text-foreground">
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="fullName" className="text-foreground text-sm">
                  Full name
                </Label>
                <Input
                  ref={nameInputRef}
                  id="fullName"
                  type="text"
                  placeholder="Your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={status === "loading"}
                  required
                  autoComplete="name"
                  className="h-10 rounded-lg border-input bg-background text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-foreground text-sm">
                  Phone number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="10-digit mobile number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={status === "loading"}
                  required
                  autoComplete="tel"
                  className="h-10 rounded-lg border-input bg-background text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  We’ll use this to reach you.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-foreground text-sm">
                  Email <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === "loading"}
                  autoComplete="email"
                  className="h-10 rounded-lg border-input bg-background text-sm"
                />
              </div>

              {status === "error" && message && (
                <Alert variant="destructive" className="text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{message}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                disabled={status === "loading" || !fullName.trim() || !phone.trim()}
                className="w-full h-10 rounded-lg font-medium text-sm"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </form>
          )}

          <p className="mt-4 text-xs text-center text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
